import { Card, Chip, Grid, Tooltip, Typography, useMediaQuery } from '@mui/material';
import React from 'react'
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTheme } from '@emotion/react';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { Language } from '@mui/icons-material';
import placeHolderAvatar from "assets/no_data_images/avatar_no_image.png"
import indianFlag from "assets/indianFlag.png"
import { convertCurrencyAmountRange, getCurrencySymbol } from 'utils/index'
import { CardContentList, CardHeader, PersonDetails } from '../../backrr_startups/viewDetails/StartupInformation'

export const isAngelInvestor = (investor) => ["angel", "super angel"].includes(
    String(investor?.investor_type).replaceAll("_", " ").toLowerCase()
);

export const investorDisplayName = (investor) => {
    const info = investor?.personal_information;
    return isAngelInvestor(investor)
        ? `${info?.first_name || ""} ${info?.last_name || ""}`.trim()
        : info?.organization_name || `${info?.first_name || ""} ${info?.last_name || ""}`.trim();
};

const withHttps = (url) => url ? (String(url).startsWith("http") ? url : "https://" + url) : null;

// Same "first + N" chip the startup page uses for business models.
const NameList = ({ items }) => {
    if (!items || items.length === 0) return '-';
    if (items.length === 1) return items[0]?.name || '-';
    return <Tooltip title={items.map((item) => <span key={item?._id}> {item?.name} <br /></span>)}>
        <Chip style={{ borderRadius: '4px' }} label={items[0]?.name + " + " + String(items.length - 1)} />
    </Tooltip>
};

const cardSx = (theme) => ({ padding: '1rem', boxShadow: 'none', borderRadius: "8px", border: `1px solid ${theme.palette.customColors.gray300}`, overflow: 'hidden' });

const InvestorInformation = ({ data: investor }) => {
    const theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
    const investor_types = useSelector(state => state.investorDetails?.investor_types);

    const info = investor?.personal_information;
    const preferences = investor?.preferences;
    const size = investor?.investment_size_preference;
    const image = isAngelInvestor(investor)
        ? investor?.custom_profile_picture_url
        : investor?.website_upload_url || investor?.custom_profile_picture_url;
    const linkedinUrl = withHttps(investor?.linkedIn_profile?.profile_url);
    const websiteUrl = withHttps(info?.organization_website);
    const typeLabel = Array.isArray(investor_types)
        ? investor_types.find(item => item?.value === String(investor?.investor_type))?.label
        : null;

    const investorInformation = [
        { label: "Investor Type", value: typeLabel || (investor?.investor_type ? String(investor?.investor_type).replaceAll("_", " ") : "-") },
        { label: "Organization", value: info?.organization_name || "-" },
        {
            label: "Investment Size",
            value: size?.min_amount && size?.max_amount
                ? `${getCurrencySymbol(size?.currency)}${convertCurrencyAmountRange(size?.min_amount, size?.currency, 0)} - ${getCurrencySymbol(size?.currency)}${convertCurrencyAmountRange(size?.max_amount, size?.currency, 0)}`
                : "-"
        },
        { label: "Previous Investments", value: investor?.previous_investment_experience?.investment_count || "-" },
        { label: "Profile Verified", value: investor?.admin_profile_view ? "Yes" : "No" },
        { label: "Signup Date", value: investor?.createdAt || "-" },
    ]

    const investmentFocus = [
        { label: "Categories", value: <NameList items={preferences?.category_id} /> },
        { label: "Sectors", value: <NameList items={preferences?.preferred_sectors} /> },
        { label: "Stages", value: <NameList items={preferences?.startup_stages} /> },
        { label: "Business Models", value: <NameList items={preferences?.business_model_id} /> },
    ]

    const contactInformation = [
        { label: "Full Name", value: [info?.title, info?.first_name, info?.last_name].filter(Boolean).join(" ") || "-" },
        { label: "Mobile", value: info?.phone_number ? `${info?.country_code ? "+" + info?.country_code + " - " : ""}${info?.phone_number}` : "-" },
        { label: "Location", value: [info?.city_id?.name, info?.state_id?.name, info?.country_id?.name].filter(Boolean).join(", ") || "-" },
        { label: "Email", value: investor?.email || "-" },
        { label: "Website URL", value: websiteUrl ? <Link target="_blank" to={websiteUrl}>{info?.organization_website}</Link> : "-" },
        { label: "LinkedIn Profile", value: linkedinUrl ? <Link target="_blank" to={linkedinUrl}>{investor?.linkedIn_profile?.profile_url}</Link> : "-" },
    ]

    const partners = (investor?.partner || [])
        .filter(item => item?.name)
        .map(item => ({
            name: item?.name,
            bio: item?.bio,
            linkedin_url: withHttps(item?.linkedin_url),
            image: item?.partner_image ? item?.partner_image_url : null,
        }));

    const sidebar = <>
        <Card sx={cardSx(theme)}>
            <CardHeader title={"Investment Focus"} />
            <CardContentList data={investmentFocus} />
        </Card>
        {partners.length > 0 && <Card sx={{ ...cardSx(theme), marginTop: "1.25rem" }}>
            <CardHeader title={"Partners"} />
            <Grid container xs={12} gap={1} alignItems={"flex-start"}>
                {partners.map((person, index) => <PersonDetails theme={theme} data={person} key={index} index={index} length={partners.length} />)}
            </Grid>
        </Card>}
    </>

    return (
        <Grid item container xs={12} spacing={"1.25rem"} display={"flex"} sx={{ paddingBottom: '2rem', flexDirection: "row" }} justifyContent={"space-between"}>
            <Grid item container xs={12} sm={12} md={7} lg={8} spacing={"1.25rem"} alignContent={"flex-start"}>
                <Grid sx={{ width: { xs: "95vw", sm: "100%" }, maxWidth: { xs: '95%', sm: "100%" }, marginInline: { xs: 'auto', sm: 0 } }} item>
                    <Card sx={cardSx(theme)}>
                        <Grid container xs={12} alignItems={"center"} spacing={2} pb={2} marginInline={"auto"}>
                            <Grid item xs={12} pl={"0 !important"}>
                                <img src={image || placeHolderAvatar} alt={investorDisplayName(investor)} style={{ height: isLargeScreen ? 150 : 110, aspectRatio: 1, objectFit: 'cover', borderRadius: "20px", margin: 'auto', display: 'flex' }} />
                            </Grid>
                            <Grid xs={12} display={"flex"} alignItems={"center"} justifyContent={"center"} gap={1}>
                                <Typography variant='title' sx={{ fontSize: "1.5rem", textAlign: 'center', marginInline: 'auto' }}>{investorDisplayName(investor) || "-"}</Typography>
                            </Grid>
                            {(info?.city_id?.name || info?.country_id?.name) && <Typography variant='body2' sx={{ fontSize: "0.8rem", textAlign: 'center', marginInline: 'auto' }}>
                                {[info?.city_id?.name, info?.country_id?.name].filter(Boolean).join(", ")}
                                {info?.country_id?.name === "India" && <span><img src={indianFlag} alt="" style={{ width: "1.2rem", marginLeft: '5px' }} /></span>}
                            </Typography>}
                            <Grid marginTop={1} xs={12} display={"flex"} alignItems={"center"} justifyContent={"center"} gap={1}>
                                {linkedinUrl && <button type='button' style={{ backgroundColor: 'transparent', padding: 0 }}><LinkedInIcon sx={{ fontSize: 30 }} onClick={() => window.open(linkedinUrl)} /></button>}
                                {websiteUrl && <button type='button' style={{ backgroundColor: 'transparent', padding: 0 }}><Language sx={{ fontSize: 30 }} onClick={() => window.open(websiteUrl)} /></button>}
                            </Grid>
                        </Grid>
                        <CardContentList data={investorInformation} style={{ marginBottom: '1.25rem' }} />
                        <Card sx={{ padding: '1rem', boxShadow: 'none', borderRadius: "8px", border: `1px solid ${theme.palette.customColors.g50}` }}>
                            <Typography fontWeight={700} fontSize={"1rem"} color={theme.palette.customColors.g200} mb={1}>
                                Bio
                            </Typography>
                            <Typography>{info?.bio || "-"}</Typography>
                        </Card>
                    </Card>
                </Grid>
                {!isLargeScreen && <Grid sx={{ width: { xs: "95vw", sm: "100%" }, maxWidth: { xs: '95%', sm: "100%" }, marginInline: { xs: 'auto', sm: 0 } }} item>
                    {sidebar}
                </Grid>}
                <Grid sx={{ width: { xs: "95vw", sm: "100%" }, maxWidth: { xs: '95%', sm: "100%" }, marginInline: { xs: 'auto', sm: 0 } }} item>
                    <Card sx={cardSx(theme)}>
                        <CardHeader title={"Contact Information"} />
                        <CardContentList data={contactInformation} />
                    </Card>
                </Grid>
            </Grid>
            {isLargeScreen && <Grid xs={12} sm={12} md={5} lg={4} item>
                {sidebar}
            </Grid>}
        </Grid>
    )
}

export default InvestorInformation
